// src/components/analytics/YearCard.js

import React, { useMemo } from "react";
import { Calendar, Clock, ListChecks, Award, Smile, List } from "lucide-react";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";
import locales from "../../utils/locales";
import parseDuration from "../../utils/parseDuration";
import { format, parseISO, startOfMonth } from "date-fns";
import { getPieDetails } from "../../utils/analyticsUtils";

function getMonthSummary(activities, categories, year, lang = "th") {
    // year fallback
    const yearNum = Number(year);
    const safeYear = isNaN(yearNum) ? new Date().getFullYear() : yearNum;
  
    // ฟังก์ชันสร้าง label ไทย/อังกฤษ
    const monthLabel = idx => {
      if (locales?.[lang]?.monthsShort && locales[lang].monthsShort[idx])
        return locales[lang].monthsShort[idx];
      const date = startOfMonth(new Date(safeYear, idx));
      return format(date, "MMM");
    };
  
    // เตรียม months ทั้ง 12
    const months = Array.from({ length: 12 }, (_, i) => {
      const date = startOfMonth(new Date(safeYear, i));
      return {
        key: monthLabel(i),
        date,
        value: 0,
      };
    });
  
    // รวมเวลาทั้งหมด (hr) ของแต่ละเดือน
    activities.forEach(a => {
      let d;
      try {
        d = parseISO(a.activity_date || a.created_at);
      } catch {
        return; // skip activity นี้
      }
      if (!d || isNaN(d.getTime()) || d.getFullYear() !== safeYear) return;
      const monthIdx = d.getMonth();
      if (monthIdx >= 0 && monthIdx < 12) {
        months[monthIdx].value += parseDuration(a.duration || 0) / 60;
      }
    });
  
    return months;
}

export default function YearCard({
  activities = [],
  categories = [],
  lang = "th",
  yearLabel = String(new Date().getFullYear()),
}) {
  const t = locales[lang];
  const yearNum = Number(yearLabel || new Date().getFullYear());
  const pieDetails = useMemo(
    () => getPieDetails(activities, categories, lang),
    [activities, categories, lang]
  );

  // Summary (เวลารวม, top category, top 3 activity, mood, count)
  const summary = useMemo(() => {
    if (!activities.length) return null;
    const totalMin = activities.reduce((sum, a) => sum + parseDuration(a.duration), 0);
    const totalHour = totalMin / 60;
    // Top category
    const catCount = {};
    activities.forEach(a => { catCount[a.category] = (catCount[a.category] || 0) + parseDuration(a.duration || 0); });
    const topCatVal = Object.entries(catCount).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topCategory = categories.find(c => c.value === topCatVal)?.label?.[lang] || "-";
    // Top 3 activities
    const actCount = {};
    activities.forEach(a => { actCount[a.activity] = (actCount[a.activity] || 0) + parseDuration(a.duration || 0); });
    const topActs = Object.entries(actCount).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([name]) => name);
    // Avg mood
    const moodToScore = { "😊": 5, "😌": 4, "😐": 3, "😔": 2, "😠": 1 };
    const scoreToMood = [null, "😠", "😔", "😐", "😌", "😊"];
    const moodArr = activities.map(a => moodToScore[a.mood]).filter(Boolean);
    const avgMood = moodArr.length > 0 ? scoreToMood[Math.round(moodArr.reduce((a, b) => a + b, 0) / moodArr.length)] : "-";
    const activityCount = activities.length;
    return { totalHour, topCategory, topActs, avgMood, activityCount };
  }, [activities, categories, lang]);

  // Pie chart ตาม category ทั้งปี
  const pieData = useMemo(() =>
    categories
      .map(cat => {
        const totalMin = activities.filter(a => a.category === cat.value)
          .reduce((sum, a) => sum + parseDuration(a.duration), 0);
        return totalMin > 0 ? {
          id: cat.label[lang], label: cat.label[lang], value: +(totalMin / 60).toFixed(2), color: cat.color
        } : null;
      }).filter(Boolean), [activities, categories, lang]
  );

  // Bar chart ข้อมูลรายเดือน
  const monthData = useMemo(
    () => getMonthSummary(activities, categories, yearNum),
    [activities, categories, yearNum]
  );
  if (!summary) {
    return (
      <div className="bg-white rounded-2xl shadow-xl px-6 py-6 mb-5 min-h-[240px] flex flex-col justify-center items-center text-gray-400">
        {lang === "th" ? "ยังไม่มีกิจกรรมในปีนี้" : "No activity this year."}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl px-6 py-6 mb-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="text-orange-500" size={22} />
        <span className="font-bold text-lg text-neutral-800">
          {yearLabel || new Date().getFullYear()}
        </span>
      </div>

      {/* Pie & Bar Chart Responsive */}
      <div className="w-full flex flex-col md:flex-row gap-8 items-stretch mb-6">
        {/* Pie Chart */}
        <div className="w-full md:w-1/2 bg-blue-50 rounded-xl p-4 shadow">
          <h4 className="font-semibold mb-2 text-blue-900">{lang === "th" ? "สัดส่วนเวลาต่อหมวด" : "Time by Category"}</h4>
          <div className="w-full h-[240px]">
            <ResponsivePie
              data={pieData}
              margin={{ top: 20, right: 20, bottom: 30, left: 20 }}
              innerRadius={0.45}
              padAngle={1.2}
              cornerRadius={5}
              colors={{ datum: "data.color" }}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
              arcLinkLabelsSkipAngle={10}
              arcLabelsTextColor="#fff"
              enableArcLinkLabels={false}
              tooltip={({ datum }) =>
                <div className="px-2 py-1 bg-white text-sm rounded shadow border border-gray-100">
                  <b>{datum.label}</b>: {datum.value} {lang === "th" ? "ชม." : "hr"}
                </div>
              }
            />
          </div>
          {pieDetails.length > 0 && (
            <div className="w-full mt-2 text-xs">
                <div className="flex font-bold border-b border-gray-200 pb-1 mb-1">
                <div className="w-1/3">{lang === "th" ? "หมวด" : "Category"}</div>
                <div className="w-1/4 text-right">{lang === "th" ? "เวลารวม" : "Total"}</div>
                <div className="w-1/6 text-right">{lang === "th" ? "ครั้ง" : "Count"}</div>
                <div className="w-1/4 text-right">{lang === "th" ? "เฉลี่ย/ครั้ง" : "Avg/Act"}</div>
                </div>
                {pieDetails.map(row => (
                <div key={row.label} className="flex items-center py-0.5 border-b last:border-0 border-gray-100">
                    <div className="w-1/3 flex items-center">
                    <span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ background: row.color }} />
                    {row.label}
                    </div>
                    <div className="w-1/4 text-right">{row.totalHour} {lang === "th" ? "ชม." : "hr"}</div>
                    <div className="w-1/6 text-right">{row.count}</div>
                    <div className="w-1/4 text-right">{row.avgHour} {lang === "th" ? "ชม." : "hr"}</div>
                </div>
                ))}
            </div>
            )}
        </div>
        {/* Bar Chart */}
        <div className="w-full md:w-1/2 bg-purple-50 rounded-xl p-4 shadow">
          <h4 className="font-semibold mb-2 text-purple-900">{lang === "th" ? "กราฟเวลารายเดือน" : "Time per Month"}</h4>
          <div className="w-full h-[240px]">
            <ResponsiveBar
              data={monthData}
              keys={["value"]}
              indexBy="key"
              margin={{ top: 20, right: 20, bottom: 30, left: 40 }}
              padding={0.28}
              colors={["#6366f1"]}
              axisBottom={{ tickSize: 0, tickPadding: 10 }}
              axisLeft={{
                tickSize: 0, tickPadding: 6,
                legend: lang === "th" ? "ชม." : "hr",
                legendPosition: "middle",
                legendOffset: -32,
              }}
              enableLabel={false}
              tooltip={({ value, indexValue }) =>
                <div className="px-2 py-1 bg-white text-sm rounded shadow border border-gray-100">
                  <b>{indexValue}</b>: {value.toFixed(1)} {lang === "th" ? "ชม." : "hr"}
                </div>
              }
            />
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="grid grid-cols-2 gap-y-2 mb-3">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-blue-400" />
          <span>{t.total_time}:</span>
          <span className="font-semibold text-blue-700">
            {Number(summary.totalHour).toFixed(1)} {lang === "th" ? "ชม." : "hr"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ListChecks size={18} className="text-pink-400" />
          <span>{t.top_category}:</span>
          <span className="font-semibold text-pink-600">{summary.topCategory || "-"}</span>
        </div>
        {/* Top 3 activities */}
        <div className="flex items-center gap-2 col-span-2">
          <Award size={18} className="text-green-400" />
          <span className="font-medium text-gray-600">
            {lang === "th" ? "Top 3 กิจกรรม:" : "Top 3 activities:"}
          </span>
          <div className="flex flex-wrap gap-1">
            {summary.topActs.length > 0
              ? summary.topActs.map((a, idx) => (
                <span
                  key={idx}
                  className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium border border-green-100"
                >
                  {a}
                </span>
              ))
              : <span>-</span>
            }
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Smile size={18} className="text-yellow-400" />
          <span>{t.mood || "Mood"}:</span>
          <span className="font-semibold text-yellow-600">{summary.avgMood || "-"}</span>
        </div>
        <div className="flex items-center gap-2">
          <List size={18} className="text-cyan-400" />
          <span>{t.activities_count || "Activities"}:</span>
          <span className="font-semibold text-cyan-700">{summary.activityCount || "-"}</span>
        </div>
      </div>
    </div>
  );
}